import React, { useEffect, useMemo } from 'react';
import { Button, notify } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';

import { formatBytes } from 'packages/pages/utils';

import { UploaderErrorType, UploaderState } from './types';
import useUploader from './useUploader';

const notificationId = 'uploader-notification';

const DISPLAY_ERRORS: { [K in UploaderErrorType]: { title: string; description?: string } } = {
    cancelled: {
        title: 'pages.uploader.title.cancel',
    },
    generic: {
        title: 'pages.uploader.title.error.generic',
    },
    mimeType: {
        title: 'pages.uploader.title.error.format',
    },
    size: {
        title: 'pages.uploader.title.error.size',
        description: 'pages.uploader.description.error.size',
    },
    resolution: {
        title: 'pages.uploader.title.error.resolution',
        description: 'pages.uploader.description.error.resolution',
    },
};

 
function getVariant({ status }: UploaderState) {
    switch (status) {
        case 'inProgress':
            return 'loading';
        case 'success':
            return 'success';
        case 'error':
            return 'error';
        default:
            
    }
}

 
function getTitle(state: UploaderState) {
    const { status, totalCount } = state;

    switch (status) {
        case 'inProgress':
            return totalCount > 1 ? 'pages.uploader.title.progress.multiple' : 'pages.uploader.title.progress';
        case 'success':
            return 'pages.uploader.title.success';
        case 'error':
            return DISPLAY_ERRORS[state.type].title;
        default:
            
    }
}

function getIsPersistent({ status }: UploaderState) {
    return status === 'inProgress';
}

function getDescription(state: UploaderState) {
    if (state.status === 'error') {
        return DISPLAY_ERRORS[state.type].description;
    }

    if (
        state.status === 'ready' ||
        state.status === 'success' ||
        state.stage === 'uploaded' ||
        state.stage === 'gettingUploadUrl' ||
        state.stage === 'statusUpdate' ||
        state.stage === 'validating'
    ) {
        return;
    }

    const loaded = formatBytes(state.loaded);
    const total = formatBytes(state.total);

    return `${loaded} / ${total}`;
}

export function getProgressPercentage(state: UploaderState) {
    if (state.status === 'inProgress' && state.stage === 'uploading') {
        return state.percent;
    }

    
}

export function getIsUploading(state: UploaderState) {
    return state.status === 'inProgress' && state.stage === 'uploading';
}

export function getUploadsTotal(state: UploaderState) {
    return state.totalCount;
}

export function getUploadingCounter(state: UploaderState) {
    return state.uploadingCount;
}

const UploaderNotifications = () => {
    const { translate } = useTranslate();
    const { state: isUploading, cancel } = useUploader({ selector: getIsUploading });
    const { state: variant } = useUploader({ selector: getVariant });
    const { state: title } = useUploader({ selector: getTitle });
    const { state: isPersistent } = useUploader({ selector: getIsPersistent });
    const { state: description } = useUploader({ selector: getDescription });
    const { state: progressPercentage } = useUploader({ selector: getProgressPercentage });
    const { state: uploadingCount } = useUploader({ selector: getUploadingCounter });
    const { state: uploadsTotal } = useUploader({ selector: getUploadsTotal });

    const getNotificationDescription = () => {
        if (variant === 'success') {
            return;
        }

        if (uploadsTotal > 1) {
            return translate('pages.uploader.description.multiple-files', { uploadingCount, uploadsTotal });
        }

        if (!description) {
            return;
        }

        return progressPercentage ? description : translate(description);
    };
    // @ts-ignore
    const { state: hideNotification } = useUploader({ selector: (state: UploaderState) => state?.hideNotification });

    const cancelButton = useMemo(
        () => (
            <Button
                onClick={() => {
                    cancel();
                }}
            >
                {translate('pages.uploader.button.cancel')}
            </Button>
        ),
        [cancel],
    );

    useEffect(() => {
        if (variant && title && !hideNotification) {
            const notifyExtraProps = uploadsTotal > 1 ? {} : { progressPercentage };

            notify({
                id: notificationId,
                variant,
                title: translate(title),
                isPersistent,
                description: getNotificationDescription(),
                button: isUploading ? cancelButton : undefined,
                ...notifyExtraProps,
            });
        }
    }, [variant, title, isPersistent, description, progressPercentage, hideNotification]);

    return null;
};

export default UploaderNotifications;
